// @ts-nocheck
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { ColumnDef } from '@tanstack/react-table';
import { toast } from 'sonner';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import { friendlyApiError } from '@/utilities/apiErrorMessage';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { cn } from '@/lib/utils/cn';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { DataTable } from '@/components/data-table/DataTable';
import { FillPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { ConfirmDialog } from '@/components/feedback/ConfirmDialog';
import { getApiCall, postApiCall } from '@/service/httpCall';
import { API_OPERATORS_LIST, API_OPERATORS_DELETE } from '@/service/url';
import { errorCheck } from '@/views/dashboard/auth/auth-utils';

const PREFIX_FIELDS = [
  { key: 'incoming_caller_from_prefix', label: 'Incoming Caller From Prefix' },
  { key: 'incoming_caller_to_prefix', label: 'Incoming Caller To Prefix' },
  { key: 'outgoing_caller_from_prefix', label: 'Outgoing Caller From Prefix' },
  { key: 'outgoing_caller_to_prefix', label: 'Outgoing Caller To Prefix' },
];


const OPERATOR_REGION_OPTIONS = [
  { value: 1, label: 'International', hint: 'Used by International trunks.' },
  { value: 0, label: 'Domestic', hint: 'Used by Domestic (India) trunks.' },
];

const EMPTY_FORM = {
  id: null,
  name: '',
  type: '',
  status: 1,
  is_international: null,
  ...Object.fromEntries(PREFIX_FIELDS.map((f) => [f.key, ''])),
};

const formatDate = (v: any) =>
  v
    ? new Date(v).toLocaleString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : '—';

const OperatorList = () => {
  const navigate = useNavigate();
  const [operators, setOperators] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState<any>(null);
  const [saving, setSaving] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<any>(null);
  const [deleting, setDeleting] = useState(false);
  const [, setToastProps] = useState<any>(null);
  const [, setShowToast] = useState(false);

  const onError = (err: any) => {
    if (typeof err !== 'object' && err.includes('TokenExpiredError')) {
      errorCheck(err, navigate, setToastProps, setShowToast);
    } else {
      toast.error(friendlyApiError(err));
    }
  };

  const fetchList = () => {
    setLoading(true);
    getApiCall(API_OPERATORS_LIST)
      .then((resp: any) => setOperators(resp || []))
      .catch(onError)
      .finally(() => setLoading(false));
  };

  useEffect(fetchList, []);

  const saveOperator = async () => {
    if (!form.name.trim() || !form.type.trim()) {
      toast.warning('Name and type are required');
      return;
    }
    if (form.is_international !== 0 && form.is_international !== 1) {
      toast.warning('Select International or Domestic');
      return;
    }
    const badPrefix = PREFIX_FIELDS.find((f) => !/^(?=.{1,18}$)\+?\d+$/.test(String(form[f.key]).trim()));
    if (badPrefix) {
      toast.warning(`${badPrefix.label} must be digits, optionally starting with +`);
      return;
    }
    setSaving(true);
    try {
      const body = {
        name: form.name.trim(),
        type: form.type.trim(),
        status: form.status,
        is_international: form.is_international,
        ...Object.fromEntries(PREFIX_FIELDS.map((f) => [f.key, String(form[f.key]).trim()])),
      };
      await postApiCall(form.id ? `${API_OPERATORS_LIST}/${form.id}` : API_OPERATORS_LIST, body);
      toast.success(form.id ? 'Operator updated successfully' : 'Operator created successfully');
      setForm(null);
      fetchList();
    } catch (err) {
      onError(err);
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    setDeleting(true);
    try {
      await postApiCall(API_OPERATORS_DELETE, { operatorIds: [pendingDelete.id] });
      toast.success('Operator deleted successfully');
      setPendingDelete(null);
      fetchList();
    } catch (err) {
      onError(err);
    } finally {
      setDeleting(false);
    }
  };

  const columns = useMemo<ColumnDef<any>[]>(
    () => [
      {
        accessorKey: 'name',
        header: 'Name',
        cell: ({ row }) => <span className="font-medium text-foreground">{row.original.name}</span>,
      },
      { accessorKey: 'type', header: 'Type' },
      {
        id: 'is_international',
        accessorFn: (row: any) => (Number(row.is_international) === 1 ? 'International' : 'Domestic'),
        header: 'International / Domestic',
        cell: ({ row }) =>
          Number(row.original.is_international) === 1 ? (
            <span className="inline-flex items-center rounded-full bg-sky-100 px-2.5 py-0.5 text-[11px] font-semibold text-sky-700 ring-1 ring-inset ring-sky-200 dark:bg-sky-950/40 dark:text-sky-300">
              International
            </span>
          ) : (
            <span className="inline-flex items-center rounded-full bg-emerald-100 px-2.5 py-0.5 text-[11px] font-semibold text-emerald-700 ring-1 ring-inset ring-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300">
              Domestic
            </span>
          ),
      },
      ...PREFIX_FIELDS.map((f) => ({
        accessorKey: f.key,
        header: f.label.replace(' Caller', '').replace(' Prefix', ''),
        cell: ({ row }) => (
          <span className="font-mono text-xs">{row.original[f.key] ?? '—'}</span>
        ),
      })),
      // {
      //   accessorKey: 'created_at',
      //   header: 'Created',
      //   cell: ({ row }) => (
      //     <span className="text-xs text-muted-foreground">{formatDate(row.original.created_at)}</span>
      //   ),
      // },
      {
        accessorKey: 'updated_at',
        header: 'Updated',
        cell: ({ row }) => (
          <span className="text-xs text-muted-foreground">{formatDate(row.original.updated_at)}</span>
        ),
      },
      {
        id: 'actions',
        header: 'Actions',
        size: 100,
        enableHiding: false,
        enableSorting: false,
        cell: ({ row }) => (
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() =>
                setForm({
                  id: row.original.id,
                  name: row.original.name || '',
                  type: row.original.type || '',
                  status: Number(row.original.status),
                  is_international:
                    row.original.is_international == null ? null : Number(row.original.is_international),
                  ...Object.fromEntries(
                    PREFIX_FIELDS.map((f) => [f.key, row.original[f.key] ?? '']),
                  ),
                })
              }
              className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-primary/10 text-primary ring-1 ring-inset ring-primary/20 transition-colors hover:bg-primary/15"
              title="Edit"
              aria-label="Edit"
            >
              <Pencil className="h-3.5 w-3.5" />
            </button>
            <button
              type="button"
              onClick={() => setPendingDelete(row.original)}
              className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-rose-100 text-rose-700 ring-1 ring-inset ring-rose-200 transition-colors hover:bg-rose-200 dark:bg-rose-950/40 dark:text-rose-300"
              title="Delete"
              aria-label="Delete"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </div>
        ),
      },
    ],
    [],
  );

  return (
    <FillPage>
      <PageHeader
        title="Operators"
        description="Telecom operators available for trunks and numbers."
        actions={
          <Button onClick={() => setForm({ ...EMPTY_FORM })}>
            <Plus className="h-4 w-4" />
            Add Operator
          </Button>
        }
      />

      <DataTable
        tableId="operators.list:v1"
        columns={columns}
        data={operators}
        isLoading={loading}
        searchPlaceholder="Search by name…"
        emptyTitle="No operators"
        emptyDescription="No operators match the current filters."
        getRowId={(row: any) => String(row.id)}
        initialColumnPinning={{ right: ['actions'] }}
        pageSize={10}
        disableExport
      />

      <Dialog open={form !== null} onOpenChange={(o) => !o && !saving && setForm(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{form?.id ? 'Edit Operator' : 'Add Operator'}</DialogTitle>
          </DialogHeader>
          {form && (
            <form
              className="grid gap-4"
              onSubmit={(e) => {
                e.preventDefault();
                saveOperator();
              }}
            >
              <div className="grid gap-1.5">
                <Label htmlFor="operator-name">Name</Label>
                <Input
                  id="operator-name"
                  value={form.name}
                  maxLength={100}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                />
              </div>
              <div className="grid gap-1.5">
                <Label htmlFor="operator-type">Type</Label>
                <Input
                  id="operator-type"
                  value={form.type}
                  maxLength={100}
                  onChange={(e) => setForm({ ...form, type: e.target.value })}
                />
              </div>
              <div className="grid gap-1.5">
                <Label>International / Domestic</Label>
                <RadioGroup
                  value={form.is_international === null ? '' : String(form.is_international)}
                  onValueChange={(v) => setForm({ ...form, is_international: Number(v) })}
                  className="grid grid-cols-1 gap-2 sm:grid-cols-2"
                >
                  {OPERATOR_REGION_OPTIONS.map((opt) => (
                    <label
                      key={opt.value}
                      htmlFor={`operator-region-${opt.value}`}
                      className={cn(
                        'flex cursor-pointer items-start gap-3 rounded-md border bg-card p-3 transition-colors',
                        form.is_international === opt.value
                          ? 'border-primary ring-1 ring-primary/40'
                          : 'hover:border-ring',
                      )}
                    >
                      <RadioGroupItem
                        value={String(opt.value)}
                        id={`operator-region-${opt.value}`}
                        className="mt-0.5"
                      />
                      <div className="min-w-0">
                        <div className="text-sm font-medium">{opt.label}</div>
                        <div className="mt-0.5 text-xs text-muted-foreground">{opt.hint}</div>
                      </div>
                    </label>
                  ))}
                </RadioGroup>
              </div>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                {PREFIX_FIELDS.map((f) => (
                  <div key={f.key} className="grid gap-1.5">
                    <Label htmlFor={`operator-${f.key}`}>{f.label}</Label>
                    <Input
                      id={`operator-${f.key}`}
                      inputMode="tel"
                      maxLength={18}
                      value={form[f.key]}
                      onChange={(e) =>
                        setForm({ ...form, [f.key]: e.target.value.replace(/[^\d+]/g, '').replace(/(?!^)\+/g, '') })
                      }
                    />
                  </div>
                ))}
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setForm(null)} disabled={saving}>
                  Cancel
                </Button>
                <Button type="submit" disabled={saving}>
                  {form.id ? 'Update' : 'Create'}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={pendingDelete !== null}
        onOpenChange={(o) => !o && setPendingDelete(null)}
        title="Delete this operator?"
        description={
          <>
            Operator <span className="font-medium text-foreground">{pendingDelete?.name}</span> will
            be deleted. Operators linked with an active trunk can't be deleted.
          </>
        }
        confirmLabel="Delete"
        tone="destructive"
        loading={deleting}
        onConfirm={confirmDelete}
      />
    </FillPage>
  );
};

export default OperatorList;
